import { useAuthStore, type ServerTourStatus } from '@/shared/stores/auth.store'
import { settingsApi } from '@/features/settings/api/settings.api'
import { saveLocalPage } from './page-invites'
import { isImpersonating } from './impersonation'

const RANK: Record<ServerTourStatus, number> = { offered: 0, started: 1, skipped: 2, dismissed: 2, completed: 3 }

/**
 * Record a page-tour status: local fallback first (so a failed write never
 * re-prompts on this browser), then the auth store in place, then the server
 * (single-key write). Never while impersonating. `completed` is never
 * downgraded and `offered` never replaces an answer — same rules as the server.
 */
export async function recordPageStatus(userId: string, tourId: string, s: ServerTourStatus, v: number): Promise<void> {
  const user = useAuthStore.getState().user
  if (user?.id !== userId) return
  if (isImpersonating(user)) return
  const current = user.onboarding?.tours?.[tourId]?.s
  if (current === 'completed') return
  if (s === 'offered' && current && current !== 'offered') return
  if (current && RANK[s] < RANK[current] && s !== 'started') return
  saveLocalPage(userId, tourId, { s })
  const onboarding = user.onboarding ?? { welcome_eligible: false, welcome: null }
  useAuthStore.setState({
    user: {
      ...user,
      onboarding: { ...onboarding, tours: { ...onboarding.tours, [tourId]: { s, v, at: new Date().toISOString() } } },
    },
  })
  try {
    await settingsApi.putTourStatus(tourId, { s, v })
  } catch {
    /* offline / 5xx: the local fallback stands */
  }
}

/** "Don't suggest page tours": global opt-out (preferences.tour_invites = 'off'). */
export async function optOutOfPageInvites(userId: string): Promise<void> {
  const user = useAuthStore.getState().user
  if (user?.id !== userId) return
  if (isImpersonating(user)) return
  const onboarding = user.onboarding ?? { welcome_eligible: false, welcome: null }
  useAuthStore.setState({ user: { ...user, onboarding: { ...onboarding, page_invites: false } } })
  try {
    localStorage.setItem(`nxtgen.onboarding.v1.pages-optout.${userId}`, '1')
  } catch {
    /* ignore */
  }
  try {
    await settingsApi.putTourInvites('off')
  } catch {
    /* the local flag stands on this browser */
  }
}

export function locallyOptedOut(userId: string): boolean {
  try {
    return localStorage.getItem(`nxtgen.onboarding.v1.pages-optout.${userId}`) === '1'
  } catch {
    return false
  }
}
